import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Trash2, Clock, User, Briefcase, MapPin, GraduationCap, Plus, Edit, X, Upload, Camera } from 'lucide-react';
import api from '../../utils/axios';
import getFullUrl from '../../utils/formatUrl';

const StudentWallManager = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPost, setEditingPost] = useState(null);
    const [colleges, setColleges] = useState([]);
    const [formData, setFormData] = useState({
        name: '', studentCode: '', address: '', course: '', college: '', 
        photo: '', duration: '', year: '', place: '', company: '', feedback: ''
    });
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        fetchPosts();
        fetchColleges();
    }, []);

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const res = await api.get('/student-posts/all');
            setPosts(res.data.data);
        } catch (err) {
            console.error("Failed to fetch posts", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchColleges = async () => {
        try {
            const res = await api.get('/colleges/active');
            console.log("Fetched active colleges:", res.data.data);
            setColleges(res.data.data || []);
        } catch (err) {
            console.error("Colleges fetch failed", err);
        }
    };

    const handleStatusUpdate = async (id, status) => {
        try {
            await api.patch(`/student-posts/${id}`, { status });
            fetchPosts();
        } catch (err) {
            alert("Failed to update status");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to permanently delete this student post?")) {
            try {
                await api.delete(`/student-posts/${id}`);
                fetchPosts();
            } catch (err) {
                alert("Delete failed");
            }
        }
    };

    const handleEdit = (post) => {
        setEditingPost(post);
        setFormData(post);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingPost(null);
        setFormData({
            name: '', studentCode: '', address: '', course: '', college: '', 
            photo: '', duration: '', year: '', place: '', company: '', feedback: ''
        });
        setIsModalOpen(true);
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        const data = new FormData();
        data.append('photo', file);
        try {
            const res = await api.post('/student-posts/upload', data);
            setFormData({ ...formData, photo: res.data.url });
        } catch (err) {
            alert("Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingPost) {
                await api.patch(`/student-posts/${editingPost._id}`, formData);
            } else {
                await api.post('/student-posts/admin-create', formData);
            }
            setIsModalOpen(false);
            fetchPosts();
        } catch (err) {
            alert("Action failed");
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#cc0000]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="bg-slate-900 text-white p-8 rounded-sm shadow-xl flex justify-between items-center">
                <div>
                    <h3 className="text-2xl font-black uppercase tracking-tighter italic">Student Wall Moderation</h3>
                    <p className="text-[10px] font-bold uppercase tracking-[4px] text-white/50">Manage approved and pending student success stories</p>
                </div>
                <div className="flex gap-4">
                    <div className="bg-white/10 px-4 py-2 rounded-sm border border-white/10 text-center">
                        <div className="text-[8px] font-black uppercase text-white/40">Total</div>
                        <div className="text-xl font-black">{posts.length}</div>
                    </div>
                    <button 
                        onClick={handleAddNew}
                        className="bg-[#cc0000] hover:bg-white hover:text-slate-900 transition-all px-6 py-2 rounded-sm flex items-center gap-2 font-black uppercase text-[10px] tracking-widest"
                    >
                        <Plus size={16} /> Add New Story
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 pb-20">
                {posts.length === 0 ? (
                    <div className="bg-white p-12 text-center border border-slate-200 rounded-sm">
                        <p className="text-slate-400 font-bold uppercase text-xs tracking-widest">No submissions found</p>
                    </div>
                ) : (
                    posts.map((post) => (
                        <div key={post._id} className="bg-white border border-slate-200 rounded-sm overflow-hidden flex flex-col md:flex-row hover:shadow-lg transition-shadow">
                            {/* Status Sidebar */}
                            <div className={`w-2 md:w-3 ${
                                post.status === 'Approved' ? 'bg-green-500' : 
                                post.status === 'Rejected' ? 'bg-red-500' : 'bg-amber-400'
                            }`}></div>

                            <div className="flex-1 p-6 flex flex-col md:flex-row gap-8">
                                <div className="flex-1 space-y-6">
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center gap-4">
                                            <div className="w-16 h-16 bg-slate-100 rounded-full overflow-hidden flex items-center justify-center border-2 border-slate-50 shadow-sm shrink-0 uppercase font-black text-slate-300">
                                                {post.photo ? (
                                                    <img src={getFullUrl(post.photo)} className="w-full h-full object-cover" alt="" />
                                                ) : (
                                                    <User size={30} />
                                                )}
                                            </div>
                                            <div>
                                                <h4 className="font-black text-slate-900 uppercase text-lg tracking-tight leading-none mb-2">{post.name}</h4>
                                                <div className="flex items-center text-[9px] font-black text-slate-400 uppercase tracking-widest gap-2">
                                                    <span>{post.studentCode}</span>
                                                    <span className="w-1 h-1 bg-slate-200 rounded-full"></span>
                                                    <span>{post.college}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest ${
                                            post.status === 'Approved' ? 'bg-green-100 text-green-700' : 
                                            post.status === 'Rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                        }`}>
                                            {post.status}
                                        </div>
                                    </div>

                                    <div className="bg-slate-50 p-6 rounded-sm border border-slate-100 relative">
                                        <p className="text-slate-600 font-medium text-sm leading-relaxed italic">
                                            "{post.feedback}"
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <div className="space-y-1">
                                            <span className="flex items-center text-[7px] font-black uppercase text-slate-400 tracking-widest gap-1">
                                                <GraduationCap size={10} /> Course
                                            </span>
                                            <p className="text-[11px] font-bold text-slate-800">{post.course}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <span className="flex items-center text-[7px] font-black uppercase text-slate-400 tracking-widest gap-1">
                                                <Clock size={10} /> Year / Dur.
                                            </span>
                                            <p className="text-[11px] font-bold text-slate-800">{post.year} ({post.duration})</p>
                                        </div>
                                        <div className="space-y-1">
                                            <span className="flex items-center text-[7px] font-black uppercase text-slate-400 tracking-widest gap-1">
                                                <Briefcase size={10} /> Company
                                            </span>
                                            <p className="text-[11px] font-bold text-slate-800">{post.company}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <span className="flex items-center text-[7px] font-black uppercase text-slate-400 tracking-widest gap-1">
                                                <MapPin size={10} /> Home
                                            </span>
                                            <p className="text-[11px] font-bold text-slate-800">{post.place}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Panel */}
                                <div className="md:w-48 flex flex-col gap-2 pt-6 md:pt-0 border-t md:border-t-0 md:border-l border-slate-100 md:pl-8 justify-center">
                                    {post.status !== 'Approved' && (
                                        <button 
                                            onClick={() => handleStatusUpdate(post._id, 'Approved')}
                                            className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 text-white rounded-sm text-[10px] font-black uppercase tracking-widest hover:bg-green-700 transition-colors shadow-lg shadow-green-600/20"
                                        >
                                            <CheckCircle size={14} /> Approve
                                        </button>
                                    )}
                                    {post.status !== 'Rejected' && (
                                        <button 
                                            onClick={() => handleStatusUpdate(post._id, 'Rejected')}
                                            className="w-full flex items-center justify-center gap-2 py-3 border border-slate-200 text-slate-600 rounded-sm text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-colors"
                                        >
                                            <XCircle size={14} /> Reject
                                        </button>
                                    )}
                                    <button 
                                        onClick={() => handleEdit(post)}
                                        className="w-full flex items-center justify-center gap-2 py-3 bg-slate-900 text-white rounded-sm text-[10px] font-black uppercase tracking-widest hover:bg-slate-700 transition-all shadow-lg shadow-slate-900/10"
                                    >
                                        <Edit size={14} /> Edit Story
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(post._id)}
                                        className="w-full flex items-center justify-center gap-2 py-3 text-red-600 rounded-sm text-[10px] font-black uppercase tracking-widest hover:bg-red-50 transition-colors"
                                    >
                                        <Trash2 size={14} /> Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Edit / Add Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white w-full max-w-2xl rounded-sm shadow-2xl relative overflow-y-auto max-h-[90vh]">
                        <button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900"><X size={24} /></button>
                        <div className="p-10">
                            <h4 className="text-2xl font-black uppercase tracking-tighter mb-8 italic">
                                {editingPost ? 'Edit Student Story' : 'Add New Story (Auto-Approved)'}
                            </h4>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Form controls same as student modal but for admin */}
                                <div className="flex flex-col items-center mb-6 pb-6 border-b border-slate-100">
                                    <div className="relative group">
                                        <div className="w-24 h-24 rounded-full bg-slate-100 overflow-hidden border-4 border-slate-50 shadow-inner flex items-center justify-center">
                                            {formData.photo ? (
                                                <img src={getFullUrl(formData.photo)} alt="Profile Preview" className="w-full h-full object-cover" />
                                            ) : (
                                                <Camera size={30} className="text-slate-300" />
                                            )}
                                        </div>
                                        <label className="absolute bottom-0 right-0 bg-[#cc0000] text-white p-2 rounded-full cursor-pointer hover:bg-slate-900 transition-colors">
                                            <Upload size={12} />
                                            <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                                        </label>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <input required placeholder="Full Name" name="name" value={formData.name} onChange={handleFormChange} className="bg-slate-50 p-4 border-b-2 border-slate-100 outline-none focus:border-[#cc0000] font-bold text-sm" />
                                    <input required placeholder="Student Code" name="studentCode" value={formData.studentCode} onChange={handleFormChange} className="bg-slate-50 p-4 border-b-2 border-slate-100 outline-none focus:border-[#cc0000] font-bold text-sm" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Select College / Institution</label>
                                    <select 
                                        required 
                                        name="college" 
                                        value={formData.college || ''} 
                                        onChange={handleFormChange} 
                                        className="w-full bg-slate-50 border-b-2 border-slate-100 p-4 outline-none focus:border-[#cc0000] font-bold text-sm text-slate-900"
                                    >
                                        <option value="">Select College</option>
                                        {(colleges || []).map((c, i) => (
                                            <option key={i} value={c.name || c}>{c.name || c}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <input required placeholder="Course" name="course" value={formData.course} onChange={handleFormChange} className="bg-slate-50 p-4 border-b-2 border-slate-100 outline-none focus:border-[#cc0000] font-bold text-sm" />
                                    <input required placeholder="Year" name="year" value={formData.year} onChange={handleFormChange} className="bg-slate-50 p-4 border-b-2 border-slate-100 outline-none focus:border-[#cc0000] font-bold text-sm" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <input required placeholder="Company" name="company" value={formData.company} onChange={handleFormChange} className="bg-slate-50 p-4 border-b-2 border-slate-100 outline-none focus:border-[#cc0000] font-bold text-sm" />
                                    <input required placeholder="Place" name="place" value={formData.place} onChange={handleFormChange} className="bg-slate-50 p-4 border-b-2 border-slate-100 outline-none focus:border-[#cc0000] font-bold text-sm" />
                                </div>
                                <textarea required placeholder="Success Story / Feedback" name="feedback" rows="4" value={formData.feedback} onChange={handleFormChange} className="w-full bg-slate-50 p-4 border-2 border-slate-50 outline-none focus:border-[#cc0000] font-bold text-sm resize-none"></textarea>
                                
                                <button disabled={uploading} type="submit" className="w-full bg-slate-900 text-white py-4 font-black uppercase tracking-[3px] text-xs hover:bg-[#cc0000] transition-all">
                                    {editingPost ? 'Save Changes' : 'Create Approved Story'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentWallManager;
